/**
 * Lazy loader for per-guide, per-language translation files.
 *
 * Guide translations used to be statically imported by guides.ts, which chained
 * ~1.5MB of JS (11 languages x ~15 guides) onto the Guides route. Now each
 * translation file is fetched on demand: only the active guide + active language
 * chunk is downloaded, and English pages download nothing at all.
 *
 * Consumers:
 * - GuideArticle.tsx calls ensureGuideTranslation() in an effect and re-renders
 *   when the translation arrives (getLocalizedGuide reads the cache synchronously).
 * - entry-server.tsx awaits ensureGuideTranslation() before renderToString so
 *   prerendered non-English guide pages ship localized content (SEO-critical).
 * - main.tsx awaits it during bootstrap when the initial URL is a non-English
 *   guide page, so the client's first render doesn't flash English.
 */
import type { GuideTranslation } from './guides';

const modules = import.meta.glob('./guide-translations/*.ts') as Record<
  string,
  () => Promise<{ default: GuideTranslation }>
>;

const cache = new Map<string, GuideTranslation>();
const inflight = new Map<string, Promise<GuideTranslation | undefined>>();

/**
 * Whether a translation file exists for this guide+language.
 *
 * Six guides have no translations at all, so their /es/, /ko/ ... URLs used to
 * serve the English text under an hreflang tag claiming otherwise -- 84 pages of
 * the same English article at 14 different URLs. Callers use this to withhold
 * hreflang and to mark the page noindex until a real translation lands, rather
 * than asking search engines to treat English as Korean.
 *
 * import.meta.glob resolves at build time, so this is a static lookup and works
 * identically during the SSR prerender pass and in the browser.
 */
export function hasGuideTranslation(slug: string, lang: string): boolean {
  if (lang === 'en') return true;
  return Boolean(modules[`./guide-translations/${slug}.${lang}.ts`]);
}

/** Languages this guide is genuinely available in, English always included. */
export function guideTranslationLangs(slug: string, allLangs: readonly string[]): string[] {
  return allLangs.filter((lang) => hasGuideTranslation(slug, lang));
}

/** Synchronous cache read; returns undefined until ensureGuideTranslation resolves. */
export function getCachedGuideTranslation(slug: string, lang: string): GuideTranslation | undefined {
  return cache.get(`${slug}:${lang}`);
}

/** Loads (once) and caches the translation for a guide+language. No-op for English. */
export async function ensureGuideTranslation(slug: string, lang: string): Promise<GuideTranslation | undefined> {
  if (!slug || lang === 'en') return undefined;
  const key = `${slug}:${lang}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const pending = inflight.get(key);
  if (pending) return pending;
  const loader = modules[`./guide-translations/${slug}.${lang}.ts`];
  if (!loader) return undefined; // no translation file for this combo — English base is used
  const promise = loader()
    .then((mod) => {
      cache.set(key, mod.default);
      return mod.default;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}
